import { useCallback, useState } from 'react';

import { FetchError } from '../../../common/api/api';
import { useJiraConnectContext } from '../JiraConnect.context';
import {
  JiraConnectAPI,
  JiraConnectRequestOptions,
  JiraConnectResponse,
  JiraConnectResult
} from '../JiraConnect.interface';
import { getJiraConnect } from './useGetJiraConnect';

interface JiraConnectError {
  err: string;
  xhr: XMLHttpRequest;
}

interface JiraConnectErrorBody {
  errorMessages: string[];
  errors: { [key: string]: any };
}

function isJiraConnectError(error: any): error is JiraConnectError {
  return typeof error.xhr !== 'undefined';
}

export async function jiraConnectRequest<Data = unknown>(
  options: JiraConnectRequestOptions,
  connect?: JiraConnectAPI
): Promise<JiraConnectResult<Data>> {
  connect = connect || (await getJiraConnect().getAsync(2));
  if (!connect) {
    return {};
  }
  // set default request method
  if (!options.type) {
    options = { ...options, type: 'GET' };
  }
  try {
    const response: JiraConnectResponse = await connect.request(options);
    // handle empty body result
    const data: Data = response.body ? JSON.parse(response.body) : {};
    console.debug('[jira-connect]\n  Response: %o\n  Data: %o', response, data);
    return { response, data };
  } catch (error) {
    console.debug('[jira-connect] Error:', error);
    const fetchError: FetchError = {
      statusCode: 500,
      error: 'Internal Server Error',
      message:
        (error instanceof Error ? error.message : '') ||
        'An error occurred. The service might be unavailable.'
    };
    if (isJiraConnectError(error)) {
      let errorBody: JiraConnectErrorBody | undefined;
      try {
        errorBody = JSON.parse(error.err);
      } catch {}
      fetchError.statusCode = error.xhr.status;
      fetchError.error = error.xhr.statusText;
      fetchError.message =
        errorBody?.errorMessages?.join(' - ') ||
        (errorBody?.errors && Object.values(errorBody.errors).join(' - ')) ||
        fetchError.message;
    }
    return { error: fetchError };
  }
}

export function useJiraConnectRequest<Data = unknown>(
  version: string | number = 2
): [
  JiraConnectResult<Data>,
  (options: JiraConnectRequestOptions) => Promise<JiraConnectResult<Data>>
] {
  const { connect } = useJiraConnectContext();
  const [result, setResult] = useState<JiraConnectResult<Data>>({});

  const request = useCallback(
    async (options: JiraConnectRequestOptions) => {
      setResult({ isLoading: true });
      // prepend /rest/api/{version}
      let { url } = options;
      url = url.startsWith('/') ? url : `/${url}`;
      url = url.startsWith('/rest/api') ? url : `/rest/api/${version}${url}`;
      options = { ...options, url };
      const result = await jiraConnectRequest<Data>(options, connect);
      setResult({ ...result, isLoading: false });
      return result;
    },
    [connect, version]
  );

  return [result, request];
}
